import { computePosition, flip, offset, shift } from '@floating-ui/dom';
import { TmElement } from '@thiememeulenhoff/lit-sdk/src';
import { html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { tag, actionMenuStyles } from './action-menu.styles';

export const PlacementTypes = [
  'top-start',
  'top',
  'top-end',
  'right-start',
  'right',
  'right-end',
  'bottom-start',
  'bottom',
  'bottom-end',
  'left-start',
  'left',
  'left-end',
] as const;
export type PlacementType = (typeof PlacementTypes)[number];

@customElement(tag)
export class ActionMenuComponent extends TmElement {
  @query('dialog')
  dialog!: HTMLDialogElement;

  @query('#trigger')
  trigger!: HTMLSlotElement;

  @property({ type: String, reflect: true })
  placement: PlacementType = 'bottom';

  opening = false;

  static override styles = actionMenuStyles;

  constructor() {
    super();
    document.body.addEventListener('click', this.#handleBackdropClick);
  }

  #handleBackdropClick = () => {
    // if element is currently opening, don't close it
    if (this.opening) {
      this.opening = false;
      return;
    }
    this.dialog?.close();
  };

  #toggle(event: Event) {
    this.#positionDialog();
    if (this.dialog.open) {
      this.dialog.close();
    } else {
      this.opening = true;
      // set dialog to inert to prevent highlighting the first element
      this.dialog.inert = true;
      this.dialog.show();
      this.dialog.inert = false;
    }

    if (event.currentTarget === this.trigger) {
      // Prevent parent components from capturing this click.
      // This is necessary in case this component is inside another component that
      // also does some action on a click. In that case you don't want a click on
      // action menu trigger to activate the parent component.
      event.stopPropagation();

      // fake click on body to activate backdrop click for other action menu's
      document.body.click();
    }
  }

  #positionDialog() {
    let triggerElement = null;
    if (this.trigger.assignedElements().length > 0) {
      const triggerElements = this.trigger.assignedElements();
      // eslint-disable-next-line prefer-destructuring
      triggerElement = triggerElements[0];
    }
    if (triggerElement === null || triggerElement === undefined) {
      return;
    }

    computePosition(triggerElement, this.dialog, {
      placement: this.placement,
      middleware: [flip(), shift(), offset(10)],
    }).then(({ x, y }) => {
      Object.assign(this.dialog.style, {
        'inset-block-start': `${y}px`,
        'inset-inline-start': `${x}px`,
      });
    });
  }

  override render() {
    return html`<div class="main">
      <slot name="trigger" @click=${this.#toggle} id="trigger"></slot>
      <dialog data-test="dialog">
        <slot name="content"></slot>
      </dialog>
    </div> `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-action-menu': ActionMenuComponent;
  }
}
